import React, { useState, useEffect } from 'react';
import type { Team, TeamMember, TeamRole, User } from '../../../../types';
import { teamService } from '../../services/teamService';
import { InviteCode } from './InviteCode';
import { MemberList } from './MemberList';
import { TeamInfo } from './TeamInfo';
import { AgentManager } from './AgentManager';
import { NotifySettings } from './NotifySettings';
import { RecurringTasks } from './RecurringTasks';
import { SeatsPanel } from '../../../billing/SeatsPanel';
import { TeamSwitcher } from './TeamSwitcher';
import { Button } from '../../../../components/ui/Button';

interface TeamManagerProps {
  isOpen: boolean;
  onClose: () => void;
  currentTeam: Team | null;
  currentUser: User;
  currentUserRole: TeamRole;
  onTeamUpdate: (team: Team) => void;
}

export const TeamManager: React.FC<TeamManagerProps> = ({
  isOpen,
  onClose,
  currentTeam,
  currentUser,
  currentUserRole,
  onTeamUpdate
}) => {
  const [activeTab, setActiveTab] = useState<'current' | 'switch'>(currentTeam ? 'current' : 'switch');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [team, setTeam] = useState<Team | null>(currentTeam);

  // 当前用户在这个团队里的真实角色（App 传下来的 currentUserRole 只是默认值）
  const myRole: TeamRole = members.find(m => m.userId === currentUser.id)?.role ?? currentUserRole;

  // 加载团队成员
  const loadMembers = async () => {
    if (!team?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const teamMembers = await teamService.getTeamMembers(team.id);
      setMembers(teamMembers || []);
    } catch (error) {
      console.error('加载团队成员失败:', error);
      setError('加载团队成员失败，请稍后重试');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理邀请码更新
  const handleInviteCodeUpdate = (newCode: string) => {
    const updatedTeam = { ...team, inviteCode: newCode };
    setTeam(updatedTeam);
    onTeamUpdate(updatedTeam);
  };

  // 重新加载团队成员
  const handleMemberUpdate = () => {
    loadMembers();
  };

  // 当团队变化时重新加载数据
  useEffect(() => {
    if (isOpen && team?.id) {
      setTeam(currentTeam);
      loadMembers();
    }
  }, [isOpen, currentTeam]);

  // 关闭模态框时重置状态
  const handleClose = () => {
    setActiveTab('current');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">团队管理</h2>
            <button 
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tab 切换 */}
        <div className="flex border-b border-gray-200">
          <button
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'current'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('current')}
          >
            <div className="flex items-center justify-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
{team ? '当前团队' : '我的团队'}
            </div>
          </button>
          <button
            className={`flex-1 py-2 px-4 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'switch'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('switch')}
          >
            <div className="flex items-center justify-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
{team ? '切换团队' : '创建/加入'}
            </div>
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-auto">
          {activeTab === 'current' ? (
            <div className="p-3 space-y-3">
              {!team ? (
                /* 没有团队时的提示 */
                <div className="text-center py-6">
                  <div className="w-12 h-12 mx-auto mb-3 bg-orange-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-medium text-gray-900 mb-2">还没有加入任何团队</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    您需要创建一个新团队或通过邀请码加入现有团队
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setActiveTab('switch')}
                  >
                    创建或加入团队
                  </Button>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-6">
                  <svg className="animate-spin w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="ml-2 text-gray-600">加载中...</span>
                </div>
              ) : (
                <>
                  {/* 团队信息 */}
                  <TeamInfo team={team} memberCount={members.filter(m => !m.user?.isAgent).length} />
                  
                  {/* 邀请码管理 */}
                  <InviteCode 
                    team={team} 
                    userRole={myRole}
                    onInviteCodeUpdate={handleInviteCodeUpdate}
                  />
                  
                  {/* 席位与计费：3 人免费，超出每人每年收费 */}
                  <SeatsPanel team={team} />

                  {/* 成员列表 */}
                  <MemberList 
                    members={members}
                    currentUser={currentUser}
                    userRole={myRole}
                    onMemberUpdate={handleMemberUpdate}
                  />

                  {/* Agent 成员：仅团队所有者/管理员 */}
                  {(myRole === 'OWNER' || myRole === 'ADMIN') && (
                    <>
                      <AgentManager team={team} />
                      <NotifySettings team={team} />
                      <RecurringTasks team={team} />
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="p-3">
              <TeamSwitcher 
                currentTeam={team}
                onTeamSwitch={(newTeam) => {
                  setTeam(newTeam);
                  onTeamUpdate(newTeam);
                  handleClose();
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};